import {
  OrderType,
  OrderTypeCode,
  DocketItem,
  Docket,
  DocketStatus,
  DocketItemStatus,
  SalesChannel,
  SALES_CHANNEL,
  App,
  TransferInfo,
  PrinterProfile,
  GroupBy,
  DEFAULT_TABLE_ABBREVIATION,
  Category,
  GroupDisplayStyle,
} from '@oolio-group/domain';
import theme from '../../common/theme';
import {
  DocketDisplayItemType,
  DEFAULT_DOCKET_NAME,
  DocketDisplayOrderItem,
  DocketDisplayItem,
  DocketDisplayBaseItem,
  DocketDisplayOtherItem,
} from '../../common/types';
import { Session } from '../../state/preferences';

import {
  DocketDisplayItemTypeHeight,
  RECALL_TEXT,
  CONTINUE,
  VOIDED_TEXT,
  DOCKET_HEADER_COLOR,
  RECALLED_TEXT,
  HeaderHeight,
  FooterHeight,
  TextCharacterCount,
  TextLineHeight,
  DOCKET_TITLE_BAR,
} from '../../common/constants';
import { removeExtraSpaces } from '../../utils/general';
import { startCase, partition, groupBy } from 'lodash';
import { translate } from '@oolio-group/localization';

/*
 example
 input-1 @orderNumber 'SP-6-10-20220815' to be formatted to output SP-6-10
 input-2 @orderNumber 'SP-6-10-20220815-1' to be formatted to output SP-6-10-1

 @example2 for online orders format is
 input-1 @orderNumber 'OL-6-20220815' to be formatted to output OL-6
*/

const groupTransfers = (docketItems: DocketItem[]) => {
  return Object.values(
    groupBy(
      docketItems,
      item =>
        `${item?.transferInfo?.toTableId}#${item?.transferInfo?.transferredBy}`,
    ),
  ) as DocketItem[][];
};

export function minifyOnlineOrderNumber(orderNumber: string): string {
  const splitedOrderNumber = orderNumber.split('-');
  const onlineOrderNum: string =
    (splitedOrderNumber?.[0] || '') + '-' + (splitedOrderNumber?.[1] || '');
  return onlineOrderNum;
}

export function minifyPosOrderNumber(orderNumber: string): string {
  const splitedOrderNumber = orderNumber.split('-');
  const prefix: string = splitedOrderNumber[0] || '';
  let result = prefix;

  if (splitedOrderNumber?.length > 1) {
    const secondPart = splitedOrderNumber[1].replace(/\D/g, '') || '';
    const thirdPart = splitedOrderNumber[2] || '';
    const fourthPart = splitedOrderNumber[4] ? `-${splitedOrderNumber[4]}` : '';
    result = `${prefix}${secondPart}-${thirdPart}${fourthPart}`;
  }

  return result;
}

export function minifyOrderNumber(orderNumber: string): string {
  if (orderNumber && orderNumber.includes('OL')) {
    return minifyOnlineOrderNumber(orderNumber);
  } else if (orderNumber) {
    return minifyPosOrderNumber(orderNumber);
  }
  return orderNumber;
}

const calculateDocketTitleBarRows = (
  tags: string[] = [],
  rushedAt?: number,
): number => {
  if (!tags.length && !rushedAt) return 0; // No tags = no rows needed

  const hasRushTag = !!rushedAt;
  const emojiCount = tags.length;

  /** Simple thresholds 7 and 9 for rush and non-rush tags
   * These numbers where chosen based on the assumption that
   * each emoji takes up a certain amount of space, and generally
   * we have 7 emojis in a single row without wrapping (with rush tag) and
   * 9 emojis without a rush tag. To reduce unwanted complexity of using
   * DOCKET_WIDTH / EmojiWIDTH, we are using these simple numbers.
   */
  let rows = 1;
  if (hasRushTag && emojiCount > 7) rows = 2;
  if (!hasRushTag && emojiCount > 9) rows = 2;

  return rows;
};

export const constructDisplayDocketTitleBar = (
  docket: Docket,
): DocketDisplayBaseItem => {
  const numberOfLines = calculateDocketTitleBarRows(
    docket.tags,
    docket.rushedAt,
  );
  const titleHeader = {
    name: DOCKET_TITLE_BAR,
    height:
      numberOfLines *
      DocketDisplayItemTypeHeight[DocketDisplayItemType.DOCKET_TITLE_BAR],
    type: DocketDisplayItemType.DOCKET_TITLE_BAR,
    docketId: docket.id,
    tags: docket.tags || [],
    rushedAt: docket.rushedAt,
  };

  return titleHeader;
};

export const constructDisplayTrainingHeader = (
  docket: Docket,
): DocketDisplayBaseItem => {
  const trainingHeader = {
    type: DocketDisplayItemType.TRAINING_DOCKET,
    name: translate('kitchenDisplay.trainingDocket'),
    height: DocketDisplayItemTypeHeight[DocketDisplayItemType.TRAINING_DOCKET],
    docketId: docket.id,
    time: docket.createdAt,
  };
  return trainingHeader;
};

export const constructDisplayDocketHeader = (
  docket: Docket,
): DocketDisplayItem[] => {
  const displayDocket = [];

  const orderInfo = {
    id: docket.id,
    type: DocketDisplayItemType.DOCKET_HEADER,
    name: DEFAULT_DOCKET_NAME,
    orderNumber:
      docket.integrationInfo?.channelOrderDisplayId ||
      minifyOrderNumber(docket.orderNumber),
    orderType: docket?.orderType?.id,
    time: docket.createdAt,
    height: DocketDisplayItemTypeHeight[DocketDisplayItemType.DOCKET_HEADER],
    section: docket?.section?.name,
    table: docket?.table?.name,
    customer: docket?.customer?.name,
    salesChannel: docket?.salesChannel?.id,
    itemStatus: docket?.itemStatus,
    source: docket?.source,
    status: docket.status,
    isTraining: !!docket.isTraining,
    tags: docket.tags || [],
    notifierItems: docket.notifierItems || [],
    rushedAt: docket.rushedAt,
  };
  displayDocket.push(orderInfo);
  return displayDocket;
};

export const constructDisplayDocketNotes = (
  docket: Docket,
): DocketDisplayItem => {
  const tempNotes = removeExtraSpaces(docket.notes || '');
  const shortNotes =
    tempNotes.length > 50 ? tempNotes.slice(0, 50) + '...' : tempNotes;
  const orderNote = {
    type: DocketDisplayItemType.ORDER_NOTE,
    name: shortNotes,
    height: DocketDisplayItemTypeHeight[DocketDisplayItemType.ORDER_NOTE],
    docketId: docket.id,
    time: docket.createdAt,
  };
  return orderNote;
};

export const constructTransferredLabel = (
  docketId: string,
  tableId: string,
  transferLabelType: DocketDisplayItemType,
): DocketDisplayItem => {
  const transferred = {
    docketId: docketId,
    type: transferLabelType,
    name: tableId,
    height: DocketDisplayItemTypeHeight[transferLabelType],
  };
  return transferred;
};

export const constructTransferredItems = (
  docket: Docket,
  assignedProfiles: PrinterProfile[],
  session: Session,
  orderTypes?: OrderType[],
  categories?: Category[],
): DocketDisplayItem[] => {
  const transfers = groupTransfers(docket.docketItems);
  const transferredItems: DocketDisplayItem[] = [];

  transfers.forEach(docketItemsGroup => {
    const [{ transferInfo = {}, orderId }] = docketItemsGroup;
    const {
      toTableId = '',
      fromTableId = '',
      toOrderId = '',
    } = transferInfo as TransferInfo;

    const isTransferredTo = toOrderId !== orderId;
    const transferLabelType = isTransferredTo
      ? DocketDisplayItemType.TRANSFERRED_TO_LABEL
      : DocketDisplayItemType.TRANSFERRED_FROM_LABEL;
    const tableId = isTransferredTo ? toTableId : fromTableId;

    transferredItems.push(
      constructTransferredLabel(docket.id, tableId, transferLabelType),
    );

    docket.docketItems = docketItemsGroup;
    transferredItems.push(
      ...constructDisplayDocketItems(
        docket,
        assignedProfiles,
        session,
        orderTypes,
        categories,
      ),
    );
  });

  return transferredItems;
};

export const constructDueAtTime = (docket: Docket): DocketDisplayItem => {
  const result = {
    type: DocketDisplayItemType.DUE_AT,
    name: docket.dueAt + '',
    height: DocketDisplayItemTypeHeight[DocketDisplayItemType.DUE_AT],
    docketId: docket.id,
    time: docket.createdAt,
  };
  return result;
};

export const constructDisplayDocketRecalledLabel = (
  docket: any,
): DocketDisplayItem => {
  const recallLabel = {
    type: DocketDisplayItemType.RECALLED_TAG,
    name: RECALLED_TEXT,
    height: DocketDisplayItemTypeHeight[DocketDisplayItemType.RECALLED_TAG],
    docketId: docket.id,
    time: docket.createdAt,
  };
  return recallLabel;
};

export const constructDisplayDocketVoidedStatus = (
  docket: Docket,
): DocketDisplayItem => {
  const voidLabel = {
    type: DocketDisplayItemType.VOID,
    name: VOIDED_TEXT,
    height: DocketDisplayItemTypeHeight[DocketDisplayItemType.VOID],
    docketId: docket.id,
    time: docket.createdAt,
  };
  return voidLabel;
};

export const constructDisplayDocketItems = (
  docket: Docket,
  assignedProfiles: PrinterProfile[],
  session?: Session,
  orderTypes?: OrderType[],
  categories?: Category[],
): DocketDisplayItem[] => {
  // Track which headers have been added
  const groupsAppended: string[] = []; // For top-level groups (seats, categories)
  const coursesAppended: Record<string, string[]> = {}; // For courses within groups, keyed by parent

  let seatName = '';
  let seatManagement = false;
  let orderTypeName = '';
  if (orderTypes?.length) {
    orderTypeName = getOrderTypeName(docket.orderType.id, orderTypes);
    seatManagement = session?.enableSeatManagement || false;
  }

  const profile = assignedProfiles.find(p => p.id === docket.printerProfileId);
  const groupingType = profile?.grouping?.groupBy;
  const displayStyle = profile?.grouping?.displayStyle;
  const isGroupingEnabled = profile?.grouping?.enabled;

  const categoryMap = new Map<string, string>();
  if (categories?.length) {
    categories.forEach(category => {
      categoryMap.set(category.id, category.name);
    });
  }

  return docket?.docketItems
    ?.map((docketItem: DocketItem, index: number) => {
      const docketDisplayItem: DocketDisplayItem[] = [];
      // Handle seat management for Dine In
      if (
        (isGroupingEnabled && groupingType === GroupBy.SEAT) ||
        (seatManagement && orderTypeName === 'Dine In')
      ) {
        seatName = docketItem?.seatNumber
          ? `S${docketItem?.seatNumber?.toString() || ''}`
          : DEFAULT_TABLE_ABBREVIATION;
      }

      // For primary course grouping
      if (
        isGroupingEnabled &&
        docketItem?.course?.id &&
        groupingType === GroupBy.COURSE
      ) {
        const courseName = docketItem?.course?.name || '';

        // Check if course has been added
        if (!groupsAppended.includes(courseName)) {
          // Add to tracking
          groupsAppended.push(courseName);

          const course = {
            type: DocketDisplayItemType.COURSE,
            name: courseName,
            height:
              displayStyle === GroupDisplayStyle.HIDDEN
                ? 0
                : DocketDisplayItemTypeHeight[DocketDisplayItemType.COURSE],
            docketId: docket.id,
            time: docket.createdAt,
            primaryGrouping: true,
            displayPreference: {
              displayStyle,
            },
          };

          if (displayStyle !== GroupDisplayStyle.HIDDEN) {
            docketDisplayItem.push(course as DocketDisplayBaseItem);
          }
        }
      }

      // For seat grouping
      if (isGroupingEnabled && seatName && groupingType === GroupBy.SEAT) {
        // Add seat header if needed
        if (!groupsAppended.includes(seatName)) {
          // Track this seat
          groupsAppended.push(seatName);

          // Add to display
          const seatHeader = {
            type: DocketDisplayItemType.SEAT_NUMBER,
            name: seatName,
            height:
              DocketDisplayItemTypeHeight[DocketDisplayItemType.SEAT_NUMBER],
            docketId: docket.id,
            primaryGrouping: true,
            displayPreference: {
              displayStyle,
            },
          };

          if (
            displayStyle !== GroupDisplayStyle.HIDDEN &&
            orderTypeName === 'Dine In' &&
            seatManagement
          ) {
            docketDisplayItem.push(seatHeader as DocketDisplayBaseItem);
          }

          // Initialize tracking for courses in this seat
          coursesAppended[seatName] = [];
        }

        // Add course header if needed
        if (docketItem?.course?.id) {
          const courseName = docketItem?.course?.name || '';

          // Initialize the array for this seat if it doesn't exist yet
          if (!coursesAppended[seatName]) {
            coursesAppended[seatName] = [];
          }

          // Only add if we haven't seen this course in this seat before
          if (!coursesAppended[seatName].includes(courseName)) {
            // Track this course for this seat
            coursesAppended[seatName].push(courseName);

            // Add to display
            const courseHeader = {
              type: DocketDisplayItemType.COURSE,
              name: courseName,
              height: DocketDisplayItemTypeHeight[DocketDisplayItemType.COURSE],
              docketId: docket.id,
              time: docket.createdAt,
              primaryGrouping: false,
            };

            docketDisplayItem.push(courseHeader as DocketDisplayBaseItem);
          }
        }
      }

      // For category grouping
      if (
        isGroupingEnabled &&
        docketItem?.product &&
        categories &&
        groupingType === GroupBy.CATEGORY
      ) {
        const categoryId = docketItem?.product?.category?.id;
        const categoryName = categoryId
          ? categoryMap.get(categoryId) || ''
          : '';

        // Add category header if needed
        if (!groupsAppended.includes(categoryName)) {
          // Track this category
          groupsAppended.push(categoryName);

          // Add to display
          const categoryHeader = {
            type: DocketDisplayItemType.CATEGORY,
            name: categoryName || '',
            height: DocketDisplayItemTypeHeight[DocketDisplayItemType.CATEGORY],
            docketId: docket.id,
            primaryGrouping: true,
            displayPreference: {
              displayStyle,
            },
          };

          if (
            categoryName?.length > 0 &&
            displayStyle !== GroupDisplayStyle.HIDDEN
          ) {
            docketDisplayItem.push(categoryHeader as DocketDisplayBaseItem);
          }

          // Initialize tracking for courses in this category
          coursesAppended[categoryName] = [];
        }

        // Add course header if needed
        if (docketItem?.course?.id) {
          const courseName = docketItem?.course?.name || '';

          // Initialize the array for this category if it doesn't exist yet
          if (!coursesAppended[categoryName]) {
            coursesAppended[categoryName] = [];
          }

          // Only add if we haven't seen this course in this category before
          if (!coursesAppended[categoryName].includes(courseName)) {
            // Track this course for this category
            coursesAppended[categoryName].push(courseName);

            // Add to display
            const courseHeader = {
              type: DocketDisplayItemType.COURSE,
              name: courseName,
              height: DocketDisplayItemTypeHeight[DocketDisplayItemType.COURSE],
              docketId: docket.id,
              time: docket.createdAt,
              primaryGrouping: false,
            };

            docketDisplayItem.push(courseHeader as DocketDisplayBaseItem);
          }
        }
      }

      // Add the product item (unchanged)
      let numberOfLinesForProduct = 1;
      if (docketItem?.product?.name) {
        numberOfLinesForProduct = getNoOfLinesForText(
          docketItem?.product?.name || '',
        );
      }
      const heightAdjustment =
        numberOfLinesForProduct > 1
          ? numberOfLinesForProduct * TextLineHeight
          : 0;
      const item: DocketDisplayOrderItem = {
        type: DocketDisplayItemType.ITEM,
        name: docketItem?.product?.name || '',
        quantity: docketItem?.quantity || 0,
        height:
          DocketDisplayItemTypeHeight[DocketDisplayItemType.ITEM] +
          heightAdjustment,
        docketId: docket.id,
        id: docketItem.id,
        itemStatus: docketItem.itemStatus,
        numberOfLinesForProduct: numberOfLinesForProduct,
        orderItemId: docketItem.orderItemId || '',
        ...(seatManagement &&
          orderTypeName === 'Dine In' &&
          (!isGroupingEnabled || groupingType !== GroupBy.SEAT) && {
            seatName: seatName.length > 0 ? seatName : undefined,
          }),
        consolidatedOrderItemIds: docketItem.consolidatedOrderItemIds,
      };
      if (docketItem.notes) {
        const numberOfLinesForNotes = Math.min(
          getNoOfLinesForText(docketItem?.notes),
          3,
        );
        const heightAdjustment =
          numberOfLinesForNotes > 1
            ? numberOfLinesForNotes * TextLineHeight
            : 0;
        item['note'] = docketItem.notes;
        item['height'] =
          item['height'] +
          DocketDisplayItemTypeHeight[DocketDisplayItemType.ITEM + 'NOTE'] +
          heightAdjustment;
        item['numberOfLinesForNotes'] = numberOfLinesForNotes;
      }
      if (docketItem?.modifiers?.length) {
        const modifiers = docketItem?.modifiers.map(modifier => {
          if (modifier.quantity && modifier.quantity > 1)
            return modifier.quantity + ' ✗ ' + modifier.name;
          else return modifier.name || '';
        });
        item['modifiers'] = modifiers;
        item['height'] =
          item['height'] +
          DocketDisplayItemTypeHeight[DocketDisplayItemType.ITEM + 'MODIFIER'] *
            modifiers.length;
      }

      docketDisplayItem.push(item);
      if (
        docket.status == DocketStatus.COMPLETED &&
        index == docket?.docketItems.length - 1 &&
        docket.itemStatus !== DocketItemStatus.VOIDED
      ) {
        const recall = {
          id: docket.id,
          docketId: docket.id,
          time: docket.createdAt,
          type: DocketDisplayItemType.RECALL_BUTTON,
          name: RECALL_TEXT,
          height:
            DocketDisplayItemTypeHeight[DocketDisplayItemType.RECALL_BUTTON],
        };
        docketDisplayItem.push(recall);
      }
      return docketDisplayItem;
    })
    .flat();
};

export const constructNotifiers = (docket: Docket): DocketDisplayItem => {
  const result = {
    type: DocketDisplayItemType.OTHER_ITEMS,
    name:
      docket.notifierItems && docket.notifierItems?.length > 1
        ? translate('kitchenDisplay.otherItems')
        : translate('kitchenDisplay.otherItem'),
    height: DocketDisplayItemTypeHeight[DocketDisplayItemType.OTHER_ITEMS],
    items: docket.docketItems,
    notifierItems: docket.notifierItems || [],
    docketId: docket.id,
    orderNumber: docket.orderNumber,
    status: docket.status,
    printerProfileId: docket.printerProfileId,
    orderTypeId: docket.orderType.id,
  } as DocketDisplayOtherItem;
  return result;
};

export const transformDocketToDocketDisplayItems = (
  docket: Docket,
  assignedProfiles: PrinterProfile[],
  session: Session,
  orderTypes: OrderType[],
  categories: Category[],
): DocketDisplayItem[] => {
  if (!docket) return [] as DocketDisplayItem[];
  const [notTransferred, transferred] = partition(
    docket.docketItems,
    item => !item.transferInfo?.fromOrderId || !item.transferInfo.toOrderId,
  );
  docket.docketItems = notTransferred;
  let displayDocket: DocketDisplayItem[] = [];
  if (docket.tags?.length || docket.rushedAt)
    displayDocket.push(constructDisplayDocketTitleBar(docket));
  if (docket.isTraining)
    displayDocket.push(constructDisplayTrainingHeader(docket));
  displayDocket.push(...constructDisplayDocketHeader(docket));
  if (
    docket.status == DocketStatus.CREATED &&
    docket.isOnline &&
    docket.dueAt
  ) {
    displayDocket.push(constructDueAtTime(docket));
  }
  if (docket.notes)
    displayDocket = [...displayDocket, constructDisplayDocketNotes(docket)];
  if (docket.itemStatus == DocketItemStatus.VOIDED)
    displayDocket = [
      ...displayDocket,
      constructDisplayDocketVoidedStatus(docket),
    ];
  if (docket.itemStatus == DocketItemStatus.RECALLED)
    displayDocket = [
      ...displayDocket,
      constructDisplayDocketRecalledLabel(docket),
    ];
  if (docket?.docketItems?.length)
    displayDocket.push(
      ...constructDisplayDocketItems(
        docket,
        assignedProfiles,
        session,
        orderTypes,
        categories,
      ),
    );
  if (transferred?.length) {
    docket.docketItems = transferred;
    displayDocket = [
      ...displayDocket,
      ...constructTransferredItems(
        docket,
        assignedProfiles,
        session,
        orderTypes,
        categories,
      ),
    ];
  }
  if (docket?.notifierItems && docket?.notifierItems?.length > 0) {
    displayDocket = [...displayDocket, constructNotifiers(docket)];
  }
  return displayDocket;
};

export const addContinueTags = (displayDockets: DocketDisplayItem[]) => {
  let height = HeaderHeight;
  displayDockets.forEach((displayDocket: DocketDisplayItem, i: number) => {
    height = height + displayDocket?.height || 0;
    const isNotHeader =
      displayDocket?.type != DocketDisplayItemType.DOCKET_HEADER;
    const heightOfDocketView = theme.window.vh - FooterHeight;
    const remianingSpaceHeight = height - heightOfDocketView;
    if (isNotHeader && remianingSpaceHeight > 0) {
      let index = i;
      if (
        remianingSpaceHeight <
        DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUE]
      )
        index = i - 1;
      height =
        HeaderHeight +
        DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUE];
      displayDockets.splice(
        index,
        0,
        {
          type: DocketDisplayItemType.CONTINUE,
          name: CONTINUE,
          height: DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUE],
        },
        {
          type: DocketDisplayItemType.CONTINUED,
          name: CONTINUE,
          height: DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUE],
        },
      );
    }
  });
  return displayDockets;
};

export const getOrderTypeName = (id: string, orderTypes: OrderType[]) => {
  return orderTypes.find(orderType => orderType.id == id)?.name || '';
};

export const getPartnerSalesChannelName = (
  id: string,
  salesChannels: SalesChannel[],
) => {
  return (
    salesChannels
      .filter(salesChannel => {
        if (
          salesChannel.name == SALES_CHANNEL.INSTORE ||
          salesChannel.name.toUpperCase() == SALES_CHANNEL.ONLINE
        )
          return false;
        return true;
      })
      .find(salesChannel => salesChannel.id == id)?.name || ''
  );
};

export const getDockerHeaderTitle = (props: {
  orderTypes: OrderType[];
  orderTypeId: string;
  tableName: string;
  sectionName: string;
  customerName: string;
  orderNumber: string;
  source: string;
}): string => {
  if (props.source === App.KIOSK) {
    return (
      props.customerName?.trim() ||
      String(+props.orderNumber.split('-')?.splice(1, 1))
    );
  }

  const DineIn = props.orderTypes.find(
    orderType => orderType.code === OrderTypeCode.DINE_IN,
  );

  if (DineIn && DineIn.id === props.orderTypeId) {
    // Show Table Details for Dine In Orders
    if (props.sectionName) {
      return [props.sectionName, props.tableName].filter(x => x).join(', ');
    }

    if (props.tableName) {
      return props.tableName;
    }
  }

  if (props.customerName) {
    // Show Customer Name Otherwise
    return startCase(props.customerName?.toLowerCase());
  }

  return DEFAULT_DOCKET_NAME;
};

export const getHeaderColorByOrderType = (
  orderTypeId: string,
  orderTypes: OrderType[],
): string => {
  if (!orderTypeId) return theme.colors.primary;
  const currentOrderType = orderTypes.find(
    orderType => orderType.id == orderTypeId,
  );
  if (currentOrderType && DOCKET_HEADER_COLOR[`${currentOrderType.code}`])
    return DOCKET_HEADER_COLOR[`${currentOrderType.code}`];
  return theme.colors.teal;
};

export const getNoOfLinesForText = (input: string) => {
  if (!input || input.length === 0) return 1;

  return input.split('\n').reduce((totalLines, line) => {
    return (
      totalLines + Math.max(1, Math.ceil(line.length / TextCharacterCount))
    );
  }, 0);
};
